/**
 * The steps onboarding and profile editing share.
 *
 * These lived inside `Onboarding.tsx` and were lifted out unchanged when
 * editing arrived. The move is what makes "editing offers exactly what
 * onboarding offers" true by construction rather than by discipline: the
 * camera, the consent copy, and the animal roster are one implementation, so
 * they cannot drift apart the next time the avatar rules change.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@tq/ui";
import { api } from "../api.js";

/** §36.2 — a square, downscaled image, to save venue bandwidth before upload. */
const CAPTURE_SIZE = 512;
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * §36.5 — the animal step, where the choice is actually a choice.
 *
 * This screen used to shuffle through nine emoji of its own and send back only
 * how many times the player had tapped Shuffle. The server took that number,
 * added it to a hash of a profile id that did not exist yet, and indexed a
 * fifteen-name roster with the result — so the character on the phone and the
 * character in the game were unrelated. Picking a llama and being issued a
 * pelican was the normal outcome, not an edge case.
 *
 * So the roster comes from the server, glyphs and all, and the name of the
 * chosen animal goes back. Shuffle stays, because it is the fun way in, but it
 * now walks the real roster and every animal is also directly tappable — the
 * player who wants the manatee should not have to shuffle until it appears.
 */
export function AnimalStep({
  busy,
  onBack,
  onError,
  onSubmit,
}: {
  busy: boolean;
  onBack: () => void;
  onError: (message: string | null) => void;
  onSubmit: (animal: string | null) => void;
}): React.ReactElement {
  const [animals, setAnimals] = useState<Array<{ name: string; emoji: string }> | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // A roster we could not fetch is not a reason to strand a player in the
  // doorway: joining still works, and the server assigns an animal as it always
  // did for a client that names none.
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .avatarOptions()
      .then((result) => {
        if (cancelled || result.animals.length === 0) return;
        setAnimals(result.animals);
        // Landing on the same animal for everyone would make the first thing a
        // player sees the thing forty other people also saw.
        setSelected(result.animals[Math.floor(Math.random() * result.animals.length)]!.name);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const current = animals?.find((animal) => animal.name === selected) ?? null;

  const shuffle = useCallback(() => {
    if (!animals || animals.length < 2) return;
    setSelected((name) => {
      const index = animals.findIndex((animal) => animal.name === name);
      return animals[(index + 1) % animals.length]!.name;
    });
  }, [animals]);

  const ready = animals != null || unavailable;

  return (
    <>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: "var(--ink)" }}>
        {unavailable
          ? "We will draw you a character in the same style as everyone else."
          : "Pick the character we will draw you in, in the same street-art style as everyone else."}
      </p>

      <div
        style={{
          display: "grid",
          placeItems: "center",
          gap: 6,
          background: "var(--surface)",
          border: "2px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          padding: 24,
        }}
      >
        <span style={{ fontSize: 64, lineHeight: 1 }} aria-hidden="true">
          {current?.emoji ?? "🎨"}
        </span>
        {/* The name, not only the glyph. Several of the roster share a glyph —
            the osprey and the eagle, the tortoise and the sea turtle — so the
            picture alone cannot say which one the player has chosen. */}
        <span
          style={{
            fontWeight: 900,
            fontSize: 15,
            color: "#fff",
            textTransform: "capitalize",
            letterSpacing: ".02em",
          }}
          aria-live="polite"
        >
          {current?.name ?? (unavailable ? "A surprise" : "Loading…")}
        </span>
      </div>

      {animals && (
        <>
          <Button variant="secondary" size="md" onClick={shuffle}>
            Shuffle
          </Button>

          <div
            role="radiogroup"
            aria-label="Choose your character"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(56px, 1fr))",
              gap: 8,
            }}
          >
            {animals.map((animal) => {
              const mine = animal.name === selected;
              return (
                <button
                  key={animal.name}
                  type="button"
                  role="radio"
                  aria-checked={mine}
                  aria-label={animal.name}
                  onClick={() => setSelected(animal.name)}
                  style={{
                    minHeight: 56,
                    fontSize: 28,
                    lineHeight: 1,
                    cursor: "pointer",
                    borderRadius: "var(--radius)",
                    // §29.2 — the selected one is not signalled by color alone:
                    // it is the one with the heavy border and the lifted fill.
                    border: mine ? "3px solid var(--gold)" : "2px solid var(--border)",
                    background: mine ? "rgba(245,180,0,.18)" : "var(--surface)",
                  }}
                >
                  <span aria-hidden="true">{animal.emoji}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <Button
        variant="primary"
        size="lg"
        disabled={busy || !ready}
        style={{ width: "100%" }}
        onClick={() => {
          onError(null);
          onSubmit(selected);
        }}
      >
        {busy ? "Joining…" : ready ? "That's me — join the game" : "Loading…"}
      </Button>
      <Button variant="ghost" size="sm" onClick={onBack}>
        Back
      </Button>
    </>
  );
}

/** §36.2 — both capture paths, upload and camera, offered together. */
export function PhotoStep({
  photo,
  onPhoto,
  onBack,
  onError,
  onSubmit,
  busy,
}: {
  photo: string | null;
  onPhoto: (value: string | null) => void;
  onBack: () => void;
  onError: (message: string | null) => void;
  onSubmit: () => void;
  busy: boolean;
}): React.ReactElement {
  // The live stream, in state rather than a ref, because the <video> element is
  // only rendered once there is one — and that is the whole bug this replaced.
  // Setting `srcObject` on the line after `setCameraOn(true)` read a ref that
  // was still null: React had not re-rendered yet, so the element did not exist
  // and the stream was never attached. The camera light came on and the circle
  // stayed black, which reads exactly like a camera that did not activate.
  // Attaching in an effect means it happens after the element is on the page.
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraNotice, setCameraNotice] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraOn = stream !== null;

  const stopCamera = useCallback(() => setStream(null), []);

  // The camera is released by the stream going away, whichever way it goes:
  // cancelled, captured, or this step unmounting because the player joined. A
  // phone whose camera light stays on afterwards is the thing to avoid, and one
  // teardown path is easier to be sure of than three.
  useEffect(() => {
    if (!stream) return;
    return () => stream.getTracks().forEach((track) => track.stop());
  }, [stream]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    // Rejects on a browser that will not autoplay even a muted inline video.
    // The stream is attached either way, and most browsers show the first frame
    // regardless, so this is not worth an error state.
    void video.play().catch(() => undefined);
  }, [stream]);

  const startCamera = useCallback(async () => {
    setCameraNotice(null);
    try {
      // §36.2 — prefer the front-facing camera.
      //
      // `mediaDevices` is absent, not merely restricted, outside a secure
      // context, so this is reached rather than left to throw a TypeError that
      // the classifier would have to unpick.
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException("Camera API unavailable", "NotSupportedError");
      }
      setStream(
        await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        }),
      );
    } catch (cause) {
      // §36.2 — the upload path is still there, so this is a redirection rather
      // than a failure. But it has to say which of the several very different
      // reasons applied: "no camera" to someone holding a phone with two of
      // them reads as a broken app, and it hides the one cause the player can
      // actually do something about.
      setStream(null);
      setCameraNotice(cameraNoticeFor(cause));
    }
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    onPhoto(squareCrop(video, video.videoWidth, video.videoHeight));
    stopCamera();
  }, [onPhoto, stopCamera]);

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      if (file.size > MAX_UPLOAD_BYTES) {
        onError("That image is too large. Please choose a smaller one.");
        return;
      }
      // §36.2 — reject non-image content by inspecting the file rather than the
      // extension: a decode failure is the inspection.
      try {
        const bitmap = await createImageBitmap(file);
        onPhoto(squareCrop(bitmap, bitmap.width, bitmap.height));
        onError(null);
      } catch {
        onError("That file is not an image we can read. Try a JPEG or PNG.");
      }
    },
    [onPhoto, onError],
  );

  return (
    <>
      {/* The live camera wins over an already-taken photo. The other order put
          "Retake" in the position of starting the camera and then showing the
          old picture instead of the viewfinder, which is the same "the camera
          did not come on" symptom by a different route. */}
      {cameraOn ? (
        <video
          ref={videoRef}
          // iOS refuses to play a video inline unless both of these are set, and
          // a refused play here is a black circle where the preview should be.
          playsInline
          muted
          autoPlay
          style={{
            width: 240,
            height: 240,
            borderRadius: "50%",
            objectFit: "cover",
            alignSelf: "center",
            background: "#000",
          }}
        />
      ) : photo ? (
        <img
          src={photo}
          alt="Your selected photo"
          style={{
            width: 200,
            height: 200,
            borderRadius: "50%",
            objectFit: "cover",
            alignSelf: "center",
          }}
        />
      ) : (
        <div
          style={{
            width: 200,
            height: 200,
            borderRadius: "50%",
            alignSelf: "center",
            display: "grid",
            placeItems: "center",
            background: "var(--surface-alt)",
            border: "2px dashed var(--border)",
            fontSize: 48,
          }}
          aria-hidden="true"
        >
          📷
        </div>
      )}

      {/* Right above the buttons, not in the page's error banner at the top:
          this is an answer to the tap the player just made, and on a phone the
          top of the page is often scrolled off by the time they get here. */}
      {cameraNotice && (
        <div
          role="status"
          style={{
            background: "var(--partial-bg)",
            border: "2px solid var(--partial)",
            borderRadius: "var(--radius)",
            padding: 12,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ink-on-light)",
          }}
        >
          {cameraNotice}
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        {cameraOn ? (
          <>
            <Button variant="primary" size="md" style={{ flex: 1 }} onClick={capture}>
              Capture
            </Button>
            <Button variant="secondary" size="md" onClick={stopCamera}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="md"
              style={{ flex: 1 }}
              onClick={() => void startCamera()}
            >
              {photo ? "Retake" : "Take a selfie"}
            </Button>
            <label
              style={{
                flex: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 44,
                border: "2px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                fontWeight: 800,
                fontSize: 14,
                color: "var(--ink-on-light)",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Upload
              <input
                type="file"
                accept="image/*"
                className="tq-visually-hidden"
                onChange={(event) => void handleFile(event.target.files?.[0])}
              />
            </label>
          </>
        )}
      </div>

      <Button
        variant="primary"
        size="lg"
        disabled={!photo || busy}
        style={{ width: "100%" }}
        onClick={onSubmit}
      >
        {busy ? "Joining…" : "Join the game"}
      </Button>
      <Button variant="ghost" size="sm" onClick={onBack}>
        Back
      </Button>
    </>
  );
}

/**
 * §36.2 — why the camera did not open, in words for the person holding it.
 *
 * Every one of these used to be "no camera available on this device", which is
 * true in exactly one of the cases below and actively misleading in the rest.
 * The one that matters most is the first: browsers only expose the camera on a
 * secure (https) origin, and Local Event Mode serves the room over plain http
 * on the venue LAN, so the API is not merely refused — it is not there at all.
 * A player told "no camera" then goes looking for a fault in their phone. The
 * security rule is not something they can work around, so it is named plainly,
 * and the upload path is offered in the same breath.
 *
 * The permission case is the other one worth separating: it is the only failure
 * the player can actually fix, and it needs a different action from them.
 */
function cameraNoticeFor(cause: unknown): string {
  // Checked before the error name: outside a secure context there is nothing to
  // permit or refuse, and every browser reports the absence differently.
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return "This game is being served over a plain local network connection, and browsers only allow the camera on secure (https) pages — so it is switched off for security reasons, not because of your phone. Upload a picture instead, or pick a character.";
  }

  const name = cause instanceof DOMException || cause instanceof Error ? cause.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Your phone blocked camera access for this page. Allow the camera for this site in your browser settings and tap “Take a selfie” again — or upload a picture instead.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No camera was found on this device — you can upload a picture instead.";
    case "NotReadableError":
    case "AbortError":
      return "Another app is using the camera. Close it and try again, or upload a picture instead.";
    default:
      return "The camera could not be started on this device — you can upload a picture instead.";
  }
}

/**
 * §36.2 — crop to square, centered, and downscale before upload so the venue
 * network carries a thumbnail rather than a full-resolution phone photo.
 */
function squareCrop(source: CanvasImageSource, width: number, height: number): string {
  const side = Math.min(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = CAPTURE_SIZE;
  canvas.height = CAPTURE_SIZE;

  const context = canvas.getContext("2d")!;
  context.drawImage(
    source,
    (width - side) / 2,
    (height - side) / 2,
    side,
    side,
    0,
    0,
    CAPTURE_SIZE,
    CAPTURE_SIZE,
  );
  return canvas.toDataURL("image/jpeg", 0.82);
}

export function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontWeight: 800, fontSize: 13, color: "#fff" }}>
        {label}
        {!required && <span style={{ color: "var(--muted)", fontWeight: 600 }}> (optional)</span>}
      </span>
      {children}
    </label>
  );
}

export function ChoiceCard({
  emoji,
  title,
  body,
  note,
  onSelect,
}: {
  emoji: string;
  title: string;
  body: string;
  note?: string;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        textAlign: "left",
        background: "var(--surface)",
        border: "2px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        padding: 16,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        minHeight: 44,
      }}
    >
      <span style={{ fontSize: 34, lineHeight: 1 }} aria-hidden="true">
        {emoji}
      </span>
      <span>
        <span style={{ display: "block", fontWeight: 900, fontSize: 16, color: "#fff" }}>
          {title}
        </span>
        <span
          style={{
            display: "block",
            fontSize: 14,
            lineHeight: 1.45,
            color: "var(--ink)",
            marginTop: 4,
          }}
        >
          {body}
        </span>
        {note && (
          <span
            style={{
              display: "block",
              fontSize: 12,
              lineHeight: 1.4,
              color: "var(--muted)",
              marginTop: 8,
            }}
          >
            {note}
          </span>
        )}
      </span>
    </button>
  );
}
